import {inject, observer, Provider} from "mobx-react";
import React from "react";
import {createRoot} from "react-dom/client";

import {
    BrowserRouter,
    createRoutesFromChildren,
    matchRoutes,
    Navigate,
    Route,
    Routes,
    useLocation,
    useNavigationType
} from "react-router-dom";
import * as Sentry from "@sentry/react";
import * as qs from "query-string";

// styles
import "./styles/app.scss";

import Auth from "./common/auth/auth";

import {Errors, ErrorsAppContext} from "Components/Common/Errors";
import {ConfirmationModal, ConfirmationModalFn} from "Components/Common/Modal";
import {BannerList} from "Components/Common/BannerList";
import {Oops} from "Components/Common/Oops";
import {SAMLAuther} from "Components/Common/SAMLAuther";

import {DashboardPage} from "Components/Dashboard";
import {Login} from "Components/Login";
import {LabelManagementPage} from "Components/Labels";
import {CommitteeManagement} from "Components/Committees";
import SuperAdminLayout from "@/layouts/enflux-admin";
import {LandingPage} from "Components/Landing";
import {UserManagement} from "Components/Users";
import ReportListPage from "@/pages/reports/page";
import {TicketSearchForm} from "Components/Reports/TicketSearchForm";
import {UserRegistration} from "Components/Users/registration";
import {Utils} from "./common/utils";
import {Spinner} from "react-bootstrap";
import GoogleTagManager from "./vendor/GoogleTagManager";
import stores, {CommonStore, FeatureGatingStore} from "./stores";


import {AttachmentRedirectPage} from "Components/Common/AttachmentRedirect";
import {LabelGroups} from "Components/Labels/LabelGroups";
import {StudentGroups} from "Components/Students/StudentGroups";
import {StudentManagement} from "Components/Students";
import {UserSettings} from "Components/Users/Settings";
import {SchoolsList} from "@/pages/internal/school-management/page";
import {FeaturesListPage} from "Components/Super/Schools/FeaturesList";
import {BannersListPage} from "Components/Super/Schools/BannersList";
import {DashboardRegistrationForm} from "Components/Super/DashboardRegistrationForm";
import {PostscriptTermsList} from "Components/Super/Ingestion/PostscriptTerms";
import {CategoriesList} from "Components/Super/Ingestion/Categories";
import EnfluxTermsListPage from "Components/EnfluxTerms";
import {CoursesList} from "Components/Super/Ingestion/Courses";
import {WhatsNewSection} from "Components/Banners/WhatsNewSection";
import {StrategicPlansScreen} from "Components/StrategicPlan/strategicPlans";
import {SWRConfig} from "swr";
import fetcher from "./common/fetcher";
import {AppProvider} from "./contexts/app";
import { ToastContextProvider } from "./contexts/toastContext";
import {CourseCatalogPage} from "Components/CourseCatalog/CourseCatalog";
import {FormUploadPage} from "Components/Super/Schools/FormUpload";
import CanvasPage from "Components/Canvas";
import CompetencyGeniePage from "Components/Categorizer";
import CompetencyMappingContainer from "Components/CompetencyMapping/CompetencyMappingContainer";
import HomePage from "@/pages/home/page";
import {DashboardList} from "Components/Dashboard/list";
import AuthenticatedLayout from "@/layouts/authenticated-layout";
import CompetencyMatchingPage from "@/pages/competencyMatching/page";

import CONFIG from "Config";
import PreferencesPage from "@/pages/preferences/page";
if (CONFIG.environment && ["test", "local", "default"].indexOf(CONFIG.environment) > -1) {
    console.info("Environment: ", CONFIG.environment);
    console.info("API URL: ", CONFIG.apis.eai);
    console.info("School ID: ", localStorage.getItem("chosen_school"));
}

console.info(`EAI UI version: ${CONFIG.version}`);

if (CONFIG.sentry && CONFIG.sentry.dsn) {
    Sentry.init({
        dsn: CONFIG.sentry.dsn,

        // This sets the sample rate to be 10%. You may want this to be 100% while
        // in development and sample at a lower rate in production
        replaysSessionSampleRate: 0.75,

        // If the entire session is not sampled, use the below sample rate to sample
        // sessions when an error occurs.
        replaysOnErrorSampleRate: 1.0,

        // This enables automatic instrumentation (highly recommended), but is not
        // necessary for purely manual usage
        integrations: [
            Sentry.reactRouterV6BrowserTracingIntegration({
                useEffect: React.useEffect,
                useLocation,
                useNavigationType,
                createRoutesFromChildren,
                matchRoutes,
            }),
            Sentry.replayIntegration({
                maskAllText: false,
                blockAllMedia: false,
            }),
        ],
        // Filter errors before sending to Sentry
        beforeSend(event, hint) {
            // Get the error message from the hint if available
            const error = hint?.originalException;

            if (error instanceof Error && error.message === "Network Error") {
                // Skip reporting the "Network Error"
                return null;
            }

            // filter out UnhandledRejection errors that have no information
            if (event !== undefined && event.exception !== undefined && event.exception.values !== undefined
                && event.exception.values.length === 1) {
                const e = event.exception.values[0];
                if (e.type === "UnhandledRejection" && e.value?.includes("Non-Error promise rejection captured with value: Object Not Found Matching Id")) {
                    return null;
                }
            }

            // Allow other events to be sent to Sentry
            return event;
        },


        // To set a uniform sample rate
        tracesSampleRate: 1.0

        // Alternatively, to control sampling dynamically
        // tracesSampler: samplingContext => {  }
    });
}

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

if (CONFIG.google_analytics && CONFIG.google_analytics.measurementId) {
    GoogleTagManager.initialize(CONFIG.google_analytics.measurementId);
}

const auth = new Auth();

interface AppWrapperProps {
    commonStore?: CommonStore
    children: any
}

interface AppWrapperState {
    shouldRender: boolean
}

@inject("commonStore")
@observer
class AppWrapper extends React.Component <AppWrapperProps, AppWrapperState>{
// The AppWrapper class is used to ensure that the school settings are loaded regardless of
//    what initial page is being rendered by the app. It needs to be within the <Provider> component

    constructor(props) {
        super(props);
        this.state = {
            shouldRender: false,
        };
    }

    componentDidMount() {
        const path = window.location.pathname;
        const regexp = new RegExp(`${CONFIG.paths.base}([^/]*)(.*)?`);
        const match = path.match(regexp);

        let doRedirect = false;

        let chosenSchool = Utils.getChosenSchool();
        let remainingPath = "";

        if (match && match[1]) {
            const possibleSchoolIdGroup = match[1];
            remainingPath = match[2] || remainingPath;
            const schoolList = Utils.getSchoolList();
            const pathContainsSchoolId = schoolList.map(school => school.toLowerCase()).includes(possibleSchoolIdGroup.toLowerCase());

            if (!pathContainsSchoolId) {
                remainingPath = `/${possibleSchoolIdGroup}${remainingPath}`;
                doRedirect = true;
            } else if (possibleSchoolIdGroup !== chosenSchool) {
                Utils.setChosenSchoolInfo(possibleSchoolIdGroup);
                chosenSchool = possibleSchoolIdGroup;
                doRedirect = true;
            }
        } else {
            doRedirect = true;
        }
        if (["", "/"].includes(remainingPath)) {
            remainingPath = "/home";
            window.location.replace(`${CONFIG.paths.base}${chosenSchool}${remainingPath}`);
            return;
        }

        if (doRedirect) {
            if (remainingPath === "/login") {
                window.location.replace(`${CONFIG.paths.base}${chosenSchool}/home`);
            } else {
                window.location.replace(`${CONFIG.paths.base}${chosenSchool}${remainingPath}`);
            }
            return;
        }

        this.setState({ shouldRender: true });

        this.props.commonStore!.getSettings();
    }

    render() {
        if (!this.state.shouldRender) {
            return null;
        }
        return (
            <React.Fragment>
                {this.props.children}
            </React.Fragment>
        );
    }
}

function Callback() {
    const location = useLocation();
    if (/access_token|id_token|error/.test(location.hash)) {
        auth.handleAuthentication();
    }
    return <div></div>;
}

interface MainProps {
    featureGatingStore?: FeatureGatingStore
}

// TODO: move Main, App and AUthElm into their own files
@inject("featureGatingStore")
@observer
class Main extends React.Component<MainProps, any> {
    componentDidMount() {
        if (this.props.featureGatingStore) {
            this.props.featureGatingStore.fetchFeatureGatingList();
        }
    }
    render() {
        const base = Utils.getRouteBase();
        const chosenSchool = Utils.getChosenSchool();
        if (!this.props.featureGatingStore) {
            return;
        }
        return (
            <>
                <SAMLAuther domain={CONFIG.auth.domain} clientId={CONFIG.auth.clientId}/>
                <AppWrapper>
                    <SentryRoutes>
                        <Route path={base + "home"} element={<HomePage />} />
                        <Route path={base + "dashboards"} element={<DashboardList />} />
                        <Route path={CONFIG.paths.base} element={<Navigate to={Utils.getLinkBase()} />} />
                        <Route path={base + "actionplans"} element={<LandingPage />}/>
                        <Route path={base + "actionplans/:actionplanId"} element={<LandingPage />} />
                        <Route path={base + "tickets"} element={<Navigate to={base + "actionplans"} />} />
                        <Route path={base + "dashboard/:dashboardName"} element={<DashboardPage />} />
                        <Route path={base + "dashboard/:dashboardName/:ticketId"} element={<DashboardPage />}/>
                        <Route path={base + "account/notifications"} element={<UserSettings />}/>
                        <Route path={base + "personal-student-groups"} element={<StudentGroups groupType={"PERSONAL"} />}/>
                        <Route path={base + "competencygenie"} element={<CompetencyGeniePage />}/>

                        {Utils.hasAdminAccess() ?
                            <Route path={base + "course-catalog"} element={<CourseCatalogPage />}/> : null}
                        {Utils.hasAdminAccess() && Utils.isFeatureActive("ui", "competency_mapping", this.props.featureGatingStore!.featureGatingList) ?
                            <Route path={base + "competency-mapping"} element={<CompetencyMappingContainer />}/> : null}
                        {Utils.hasAdminAccess() &&
                            <Route path={base + "competency-matching"} element={<CompetencyMatchingPage />}/> }
                        {Utils.hasAdminAccess() ?
                            <Route path={base + "users"} element={<UserManagement />}/> : null}
                        {Utils.hasAdminAccess() ?
                            <Route path={base + "labels"} element={<LabelManagementPage />}/> : null}
                        {Utils.hasAdminAccess() ?
                            <Route path={base + "labelgroups"} element={<LabelGroups />}/> : null}
                        {Utils.isSuperAdmin() ?
                            <Route path={base + "students"} element={<StudentManagement />}/> : null}
                        {Utils.hasAdminAccess() ?
                            <Route path={base + "studentgroups"} element={<StudentGroups />}/> : null}
                        {Utils.hasAdminAccess() ?
                            <Route path={base + "preferences"} element={<PreferencesPage />}/> : null}
                        {Utils.hasAdminAccess() ?
                            <Route path={base + "enflux-terms"} element={<EnfluxTermsListPage />} /> : null }
                        {Utils.hasAdminAccess() ?
                            <Route path={base + "committees"} element={<CommitteeManagement />}/> : null}
                        {Utils.hasAdminAccess() ?
                            <Route path={base + "institutionaleffectiveness"} element={<StrategicPlansScreen />}/> : null}
                        {Utils.hasAdminAccess() ?
                            <Route path={base + "canvas"} element={<CanvasPage />}/> : null}
                        {Utils.hasAdminAccess() ?
                            <Route path={base + "reports"} element={<ReportListPage />} /> : null}
                        {Utils.hasAdminAccess() ?
                            <Route path={base + "reports/create"} element={<TicketSearchForm />}/> : null}
                        {Utils.isSuperAdmin() ?
                            <Route path={base + "internal"} element={<SuperAdminLayout />}>
                                <Route path={ base + "internal/schools/manage" } element={<SchoolsList />}/>
                                <Route path={ base + "internal/schools/features" } element={<FeaturesListPage />}/>
                                <Route path={ base + "internal/schools/banners" } element={<BannersListPage />}/>
                                <Route path={ base + "internal/schools/official-student-list-upload" } element={<FormUploadPage />}/>
                                <Route path={ base + "internal/dashboards/register" } element={<DashboardRegistrationForm />}/>
                                <Route path={ base + "internal/ingestion/postscript/terms" } element={<PostscriptTermsList />}/>
                                <Route path={ base + "internal/ingestion/categories" } element={<CategoriesList />}/>
                                <Route path={ base + "internal/ingestion/courses" } element={<CoursesList />}/>
                                <Route path={ base + "internal" } element={<Navigate to={`${CONFIG.paths.base}${chosenSchool}/internal/schools/manage` } />}/>
                            </Route> : null}

                        <Route path={base + "login"} element={<Navigate to={`/${chosenSchool}`} />}/>
                        <Route path={CONFIG.paths.base + "callback"} element={<Callback />}/>
                        <Route path={base + "files/:fileId"} element={<AttachmentRedirectPage />}/>
                        <Route path={base + "whats-new"} element={<WhatsNewSection />}/>
                        <Route path="*" element={<Oops />}/>
                    </SentryRoutes>
                </AppWrapper>
                <ConfirmationModal />
                <ConfirmationModalFn />
                <Errors/>
                <ErrorsAppContext />
            </>
        );
    }
}

interface AppProps {
    auth: Auth
}

const App = function ({auth}: AppProps) {
    const isAuthenticated = auth.isAuthenticated();

    return isAuthenticated ? (
        <Provider {...stores}>
            <AuthenticatedLayout auth={auth}>
                <Main />
            </AuthenticatedLayout>
        </Provider>
    ) : (
        <AuthorizerElm auth={auth}/>
    );
};

function RenderActivate() {
    const location = useLocation();
    const token = location.search ? location.search.split("activateToken=")[1].split("&")[0] : false;
    if(token){
        return (<UserRegistration activateToken={token} />);
    }
    Sentry.captureMessage(`User tried to activate at ${window.location.href} but has no activateToken!`);

    return (<Navigate to={CONFIG.paths.base + "login"} />);
}

function RenderClearAllStorage() {
    Utils.clearAllStorage();
    return (<Navigate to={CONFIG.paths.base + "login"}/>);
}

interface AuthorizerElmProps {
    auth: Auth
}

function AuthorizerElm({auth}: AuthorizerElmProps) {
    const location = window.location;

    const ensureAuth = () => {
        const { isAuthenticated } = auth;
        if (!isAuthenticated()) {
            auth.loginRedirect();
        }
    };

    // ensure Auth
    if (location.pathname !== "/activate") {
        ensureAuth();
    }

    const { isAuthenticated } = auth;
    const shouldShowSpinner = !(!isAuthenticated() || window.location.pathname === "/activate" || window.location.pathname === "/" || window.location.pathname === "/login");

    return (
        <div>
            <Provider {...stores}>
                <BannerList isAuthenticated={false}/>
                <SentryRoutes>
                    <Route path={CONFIG.paths.base + "callback"} element={<Callback />} />
                    <Route path={CONFIG.paths.base + "activate"} element={<RenderActivate />} />
                    <Route path={CONFIG.paths.base + "clearstorage"} element={<RenderClearAllStorage /> } />
                    <Route path={CONFIG.paths.base + "login"} element={<Login />} />
                </SentryRoutes>
            </Provider>
            {shouldShowSpinner ?
                <div style={{ textAlign: "center", paddingTop: 60 }}>
                    <Spinner animation="border" variant="secondary" />
                </div> : null}
        </div>
    );
}

function Redirector() {
    const location = useLocation();
    React.useEffect(() => {
        const regexes = [/https:\/\/ei[0-9]+\.examsoft.com\/.+/i, /https:\/\/enflux.zendesk.com\/.+/i];
        const args = qs.parse(location.search);

        const url = args.url && typeof args.url === "string" ? args.url : "/";
        const isLegit = regexes.find((re) => { return re.test(url); });

        if (isLegit) {
            window.location.href = url;
        } else {
            window.location.href = "/";
        }
    }, [location]);


    return <div />;
}

// Accessibility: Track keyboard vs mouse navigation for focus outline
let handleFirstTab, handleMouseDownOnce;
if (typeof window !== "undefined" && typeof document !== "undefined") {
    handleFirstTab = function (e) {
        if (e.key === "Tab") {
            document.body.classList.add("user-is-tabbing");
            window.removeEventListener("keydown", handleFirstTab);
            window.addEventListener("mousedown", handleMouseDownOnce);
        }
    };
    handleMouseDownOnce = function () {
        document.body.classList.remove("user-is-tabbing");
        window.removeEventListener("mousedown", handleMouseDownOnce);
        window.addEventListener("keydown", handleFirstTab);
    };
    window.addEventListener("keydown", handleFirstTab);
}

const container = document.getElementById("root");
const root = createRoot(container!);

root.render(
    <AppProvider>
        <ToastContextProvider>
            <SWRConfig
                value={{
                    refreshInterval: 600000,
                    fetcher: fetcher,
                    revalidateOnFocus: false,
                }}>
                <BrowserRouter>
                    <SentryRoutes>
                        <Route path={CONFIG.paths.base + "do_redirect"} element={<Redirector />} />
                        <Route path="*" element={<App auth={auth} />} />
                    </SentryRoutes>
                </BrowserRouter>
            </SWRConfig>
        </ToastContextProvider>
    </AppProvider>
);
